import { useState, useEffect, useMemo } from 'react';
import { supabase } from '../../lib/supabase';
import { Search, Plus, CreditCard as Edit2, Trash2, Users, Calendar, BookOpen, UserPlus, Copy, FilterX, AlertTriangle, Layers, ClipboardCheck } from 'lucide-react';
import { CourseModal } from '../modals/CourseModal';
import { CreateCourseFromTemplateModal } from '../modals/CreateCourseFromTemplateModal';
import { EnrollmentModal } from '../modals/EnrollmentModal';
import { EditCourseProtocolModal } from '../modals/EditCourseProtocolModal';
import { Button } from '../ui/Button';
import { PageHeader } from '../ui/PageHeader';
import { Spinner } from '../ui/Spinner';
import { EmptyState } from '../ui/EmptyState';
import { useConfirm } from '../ui/ConfirmDialog';
import { toast } from '../ui/Toaster';

interface Course {
  id: string;
  name: string;
  description: string;
  start_date: string;
  end_date: string;
  status: string;
  docente_id: string;
  thumbnail_url?: string;
  secondary_docente_id?: string;
  evaluation_1_date?: string;
  evaluation_2_date?: string;
  evaluation_3_date?: string;
  final_evaluation_date?: string;
  profiles?: {
    full_name: string;
  };
}

const STATUS_LABEL: Record<string, string> = {
  active: 'Activo',
  completed: 'Completado',
  draft: 'Borrador',
  archived: 'Archivado',
};

export function CoursesManagement() {
  const confirm = useConfirm();
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFromTemplateOpen, setIsFromTemplateOpen] = useState(false);
  const [selectedCourseId, setSelectedCourseId] = useState<string | undefined>();
  const [isEnrollmentModalOpen, setIsEnrollmentModalOpen] = useState(false);
  const [enrollmentCourse, setEnrollmentCourse] = useState<{ id: string; name: string } | null>(null);
  const [protocolCourse, setProtocolCourse] = useState<{ id: string; name: string } | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterFromDate, setFilterFromDate] = useState('');  // YYYY-MM-DD
  const [filterToDate,   setFilterToDate]   = useState('');  // YYYY-MM-DD
  const [stats, setStats] = useState({ total: 0, active: 0, completed: 0, enrollments: 0 });

  useEffect(() => {
    loadCourses();
    loadStats();
  }, []);

  const loadCourses = async () => {
    try {
      const { data, error } = await supabase
        .from('courses')
        .select(`
          *,
          profiles:docente_id (full_name)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setCourses(data || []);
    } catch (error) {
      console.error('Error loading courses:', error);
      toast.error('No se pudieron cargar los cursos');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const { data: coursesData } = await supabase.from('courses').select('status');
      const { data: enrollmentsData } = await supabase.from('course_enrollments').select('id');

      setStats({
        total: coursesData?.length || 0,
        active: coursesData?.filter((c) => c.status === 'active').length || 0,
        completed: coursesData?.filter((c) => c.status === 'completed').length || 0,
        enrollments: enrollmentsData?.length || 0,
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const handleDelete = async (course: Course) => {
    const ok = await confirm({
      title: 'Eliminar curso',
      description: `Se eliminará “${course.name}” junto con todos sus módulos y asignaciones. Esta acción no se puede deshacer.`,
      confirmText: 'Eliminar',
      cancelText: 'Cancelar',
      tone: 'danger',
    });
    if (!ok) return;

    try {
      const { error } = await supabase.from('courses').delete().eq('id', course.id);
      if (error) throw error;
      toast.success('Curso eliminado');
      await Promise.all([loadCourses(), loadStats()]);
    } catch (error: any) {
      console.error('Error deleting course:', error);
      toast.error('Error al eliminar el curso: ' + (error.message || 'Intenta nuevamente'));
    }
  };

  const handleEdit = (courseId: string) => {
    setSelectedCourseId(courseId);
    setIsModalOpen(true);
  };

  const handleNew = () => {
    setSelectedCourseId(undefined);
    setIsModalOpen(true);
  };

  const handleDuplicate = async (course: Course) => {
    const ok = await confirm({
      title: 'Duplicar curso',
      description: `Se creará una copia de “${course.name}” con todos sus módulos y asignaciones de materiales.`,
      confirmText: 'Duplicar',
      cancelText: 'Cancelar',
      tone: 'info',
    });
    if (!ok) return;

    try {
      const { data: source, error: courseError } = await supabase
        .from('courses')
        .select('*')
        .eq('id', course.id)
        .maybeSingle();
      if (courseError) throw courseError;
      if (!source) throw new Error('Curso no encontrado');

      const newCourse = {
        name: `${source.name} (Copia)`,
        description: source.description,
        docente_id: source.docente_id,
        secondary_docente_id: source.secondary_docente_id,
        start_date: source.start_date,
        end_date: source.end_date,
        evaluation_1_date: source.evaluation_1_date,
        evaluation_2_date: source.evaluation_2_date,
        evaluation_3_date: source.evaluation_3_date,
        final_evaluation_date: source.final_evaluation_date,
        thumbnail_url: source.thumbnail_url,
        status: 'draft',
      };

      const { data: newCourseData, error: insertError } = await supabase
        .from('courses')
        .insert([newCourse])
        .select()
        .maybeSingle();
      if (insertError) throw insertError;
      if (!newCourseData) throw new Error('Error al crear el curso duplicado');

      const { data: modules, error: modulesError } = await supabase
        .from('modules')
        .select('*')
        .eq('course_id', course.id)
        .order('order_index');
      if (modulesError) throw modulesError;

      if (modules && modules.length > 0) {
        const newModules = modules.map((m) => ({
          course_id: newCourseData.id,
          name: m.name,
          description: m.description,
          objective: m.objective,
          docente_id: m.docente_id,
          order_index: m.order_index,
          start_date: m.start_date,
          end_date: m.end_date,
          zoom_link: m.zoom_link,
          status: m.status,
        }));

        const { data: insertedModules, error: insertModulesError } = await supabase
          .from('modules')
          .insert(newModules)
          .select();
        if (insertModulesError) throw insertModulesError;
        if (!insertedModules || insertedModules.length === 0) {
          throw new Error('Error al duplicar los módulos');
        }

        for (let i = 0; i < modules.length; i++) {
          const oldModuleId = modules[i].id;
          const newModuleId = insertedModules[i].id;

          const { data: moduleMaterials } = await supabase
            .from('module_materials')
            .select('material_id')
            .eq('module_id', oldModuleId);

          if (moduleMaterials && moduleMaterials.length > 0) {
            const newModuleMaterials = moduleMaterials.map((mm) => ({
              module_id: newModuleId,
              material_id: mm.material_id,
            }));
            await supabase.from('module_materials').insert(newModuleMaterials);
          }
        }
      }

      toast.success(`Curso duplicado: ${newCourseData.name}`);
      await Promise.all([loadCourses(), loadStats()]);
    } catch (error: any) {
      console.error('Error duplicating course:', error);
      toast.error('Error al duplicar: ' + (error.message || 'Intenta nuevamente'));
      await loadCourses();
    }
  };

  const filteredCourses = useMemo(() => {
    const fromMs = filterFromDate ? new Date(filterFromDate).getTime() : null;
    // El "hasta" incluye todo el día seleccionado (23:59:59).
    const toMs   = filterToDate ? new Date(filterToDate + 'T23:59:59').getTime() : null;
    const term = searchTerm.toLowerCase();

    return courses.filter((course) => {
      // Filtro de texto (nombre o descripción)
      if (term) {
        const matches =
          course.name.toLowerCase().includes(term) ||
          course.description?.toLowerCase().includes(term);
        if (!matches) return false;
      }
      // Filtro de fecha — usa start_date del curso. Si no tiene fecha y se
      // está filtrando por rango, lo dejamos fuera (no se puede ubicar).
      if (fromMs !== null || toMs !== null) {
        if (!course.start_date) return false;
        const t = new Date(course.start_date).getTime();
        if (fromMs !== null && t < fromMs) return false;
        if (toMs   !== null && t > toMs)   return false;
      }
      return true;
    });
  }, [courses, searchTerm, filterFromDate, filterToDate]);

  const hasFilter = !!(searchTerm || filterFromDate || filterToDate);
  const clearFilters = () => {
    setSearchTerm('');
    setFilterFromDate('');
    setFilterToDate('');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 p-4 sm:p-6 lg:p-8">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 sm:p-6 lg:p-8 overflow-y-auto">
      <PageHeader
        eyebrow="Gestión"
        eyebrowIcon={BookOpen}
        title="Gestión de cursos"
        description="Crea, edita, duplica e inscribe alumnos en los cursos de la plataforma."
        actions={
          // Dos botones: "Desde plantilla" (flujo rápido nuevo) + "Nuevo curso"
          // (flujo tradicional, sin plantilla, sigue funcionando como antes).
          <div className="flex items-center gap-2">
            <Button
              variant="lime"
              leftIcon={<Layers className="w-4 h-4" />}
              onClick={() => setIsFromTemplateOpen(true)}
            >
              Desde plantilla
            </Button>
            <Button variant="ghost" leftIcon={<Plus className="w-4 h-4" />} onClick={handleNew}>
              Nuevo curso
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 md:gap-6">
        <StatCard label="Total cursos" value={stats.total} icon={BookOpen} iconClass="text-cubus-lime" />
        <StatCard label="Activos" value={stats.active} icon={BookOpen} iconClass="text-emerald-400" />
        <StatCard label="Completados" value={stats.completed} icon={BookOpen} iconClass="text-cyan-400" />
        <StatCard label="Inscripciones" value={stats.enrollments} icon={Users} iconClass="text-violet-400" />
      </div>

      <section className="glass-surface rounded-2xl p-6 border border-white/[0.06]" role="search" aria-label="Listado de cursos">
        <div className="mb-6 grid grid-cols-1 md:grid-cols-[1fr_auto_auto_auto] gap-3 items-end">
          <div>
            <label htmlFor="course-search" className="sr-only">Buscar cursos</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-subtle" aria-hidden="true" />
              <input
                id="course-search"
                type="text"
                placeholder="Buscar cursos por nombre o descripción…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-black/30 border border-white/[0.08] rounded-lg text-white placeholder:text-subtle focus:outline-none focus:ring-2 focus:ring-cubus-lime/60 focus:border-cubus-lime/40 transition-colors"
              />
            </div>
          </div>
          <div>
            <label htmlFor="course-from" className="block text-[11px] text-white/50 mb-1">Desde</label>
            <input
              id="course-from"
              type="date"
              value={filterFromDate}
              onChange={(e) => setFilterFromDate(e.target.value)}
              className="w-full md:w-[160px] px-3 py-2.5 bg-black/30 border border-white/[0.08] rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-cubus-lime/60"
              title="Filtrar cursos cuya fecha de inicio sea desde esta fecha"
            />
          </div>
          <div>
            <label htmlFor="course-to" className="block text-[11px] text-white/50 mb-1">Hasta</label>
            <input
              id="course-to"
              type="date"
              value={filterToDate}
              onChange={(e) => setFilterToDate(e.target.value)}
              className="w-full md:w-[160px] px-3 py-2.5 bg-black/30 border border-white/[0.08] rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-cubus-lime/60"
              title="Filtrar cursos cuya fecha de inicio sea hasta esta fecha"
            />
          </div>
          {hasFilter && (
            <button
              type="button"
              onClick={clearFilters}
              className="px-3 py-2.5 text-xs text-white/60 hover:text-white border border-white/[0.08] rounded-lg hover:bg-white/[0.04] transition-colors flex items-center gap-1.5"
              title="Limpiar filtros"
            >
              <FilterX className="w-3.5 h-3.5" />
              Limpiar
            </button>
          )}
        </div>

        {filteredCourses.length === 0 ? (
          searchTerm ? (
            <EmptyState
              icon={FilterX}
              title="Sin resultados"
              description={`No encontramos cursos que coincidan con “${searchTerm}”.`}
              action={
                <Button variant="ghost" onClick={() => setSearchTerm('')}>
                  Limpiar búsqueda
                </Button>
              }
            />
          ) : (
            <EmptyState
              icon={BookOpen}
              title="Todavía no hay cursos"
              description="Crea el primer curso para empezar a organizar módulos, materiales y alumnos."
              action={
                <Button variant="lime" leftIcon={<Plus className="w-4 h-4" />} onClick={handleNew}>
                  Crear primer curso
                </Button>
              }
            />
          )
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {filteredCourses.map((course) => (
              <article
                key={course.id}
                className="bg-white/[0.03] rounded-xl overflow-hidden border border-white/[0.06] hover:border-cubus-lime/40 transition-all group"
              >
                <div className="relative w-full h-44 bg-black/40 overflow-hidden">
                  {course.thumbnail_url ? (
                    <img
                      src={course.thumbnail_url}
                      alt=""
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-cubus-lime/10 to-transparent">
                      <BookOpen className="w-14 h-14 text-white/20" aria-hidden="true" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent pointer-events-none" />
                  <span
                    className={`absolute top-3 right-3 chip ${
                      course.status === 'active'
                        ? 'chip-emerald'
                        : course.status === 'completed'
                        ? 'chip-cyan'
                        : 'chip-violet'
                    }`}
                  >
                    {STATUS_LABEL[course.status] || course.status}
                  </span>
                </div>

                <div className="p-5">
                  <h3 className="text-lg font-semibold text-white mb-1 group-hover:text-cubus-lime transition-colors line-clamp-1">
                    {course.name}
                  </h3>
                  <p className="text-muted text-sm line-clamp-2 mb-4 min-h-[2.5rem]">{course.description || 'Sin descripción.'}</p>

                  <dl className="space-y-1.5 mb-5 text-sm">
                    <div className={`flex items-center gap-2 ${course.profiles?.full_name ? 'text-subtle' : 'text-rose-400'}`}>
                      {course.profiles?.full_name ? (
                        <Users className="w-4 h-4" aria-hidden="true" />
                      ) : (
                        <AlertTriangle className="w-4 h-4" aria-hidden="true" />
                      )}
                      <dt className="sr-only">Docente</dt>
                      <dd className="truncate">
                        {course.profiles?.full_name || (
                          <span className="font-medium">⚠ Sin docente asignado</span>
                        )}
                      </dd>
                    </div>
                    <div className="flex items-center gap-2 text-subtle">
                      <Calendar className="w-4 h-4" aria-hidden="true" />
                      <dt className="sr-only">Fechas</dt>
                      <dd>
                        {new Date(course.start_date).toLocaleDateString()} – {new Date(course.end_date).toLocaleDateString()}
                      </dd>
                    </div>
                  </dl>

                  <div className="space-y-2 pt-4 border-t border-white/[0.06]">
                    <Button
                      variant="lime"
                      fullWidth
                      leftIcon={<UserPlus className="w-4 h-4" />}
                      onClick={() => {
                        setEnrollmentCourse({ id: course.id, name: course.name });
                        setIsEnrollmentModalOpen(true);
                      }}
                    >
                      Gestionar inscripciones
                    </Button>
                    <Button
                      variant="glass"
                      fullWidth
                      leftIcon={<ClipboardCheck className="w-4 h-4" />}
                      onClick={() => setProtocolCourse({ id: course.id, name: course.name })}
                    >
                      Protocolo BIM
                    </Button>
                    <div className="grid grid-cols-3 gap-2">
                      <Button
                        variant="glass"
                        size="sm"
                        leftIcon={<Copy className="w-3.5 h-3.5" />}
                        onClick={() => handleDuplicate(course)}
                        aria-label={`Duplicar curso ${course.name}`}
                      >
                        Duplicar
                      </Button>
                      <Button
                        variant="glass"
                        size="sm"
                        leftIcon={<Edit2 className="w-3.5 h-3.5" />}
                        onClick={() => handleEdit(course.id)}
                        aria-label={`Editar curso ${course.name}`}
                      >
                        Editar
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        leftIcon={<Trash2 className="w-3.5 h-3.5" />}
                        onClick={() => handleDelete(course)}
                        aria-label={`Eliminar curso ${course.name}`}
                      >
                        Eliminar
                      </Button>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      <CourseModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedCourseId(undefined);
        }}
        onSuccess={() => {
          loadCourses();
          loadStats();
        }}
        courseId={selectedCourseId}
      />

      <CreateCourseFromTemplateModal
        isOpen={isFromTemplateOpen}
        onClose={() => setIsFromTemplateOpen(false)}
        onSuccess={() => {
          loadCourses();
          loadStats();
        }}
      />

      {enrollmentCourse && (
        <EnrollmentModal
          isOpen={isEnrollmentModalOpen}
          onClose={() => {
            setIsEnrollmentModalOpen(false);
            setEnrollmentCourse(null);
          }}
          onSuccess={() => {
            loadCourses();
            loadStats();
          }}
          courseId={enrollmentCourse.id}
          courseName={enrollmentCourse.name}
        />
      )}

      <EditCourseProtocolModal
        open={!!protocolCourse}
        onClose={() => setProtocolCourse(null)}
        courseId={protocolCourse?.id ?? null}
        courseName={protocolCourse?.name}
      />
    </div>
  );
}

function StatCard({
  label,
  value,
  icon: Icon,
  iconClass,
}: {
  label: string;
  value: number;
  icon: typeof BookOpen;
  iconClass: string;
}) {
  return (
    <div className="glass-surface rounded-xl p-5 border border-white/[0.06] m-4 sm:m-6 lg:m-8 overflow-y-auto flex-1">
      <div className="flex items-center justify-between mb-1.5">
        <h3 className="text-subtle text-sm font-medium">{label}</h3>
        <Icon className={`w-5 h-5 ${iconClass}`} aria-hidden="true" />
      </div>
      <p className="text-3xl font-bold text-white tracking-tight">{value}</p>
    </div>
  );
}
